import { useEffect, useState, KeyboardEvent, MouseEvent } from 'react';
import moment from 'moment';
import { compareVersions } from 'compare-versions';
import { Progress, Table, TableColumn } from '@backstage/core-components';
import { useEntity } from '@backstage/plugin-catalog-react';
import { useApi } from '@backstage/core-plugin-api';
import { ContainerImagesEntity } from '@runway/plugin-container-images-common';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Chip from '@material-ui/core/Chip';
import Container from '@material-ui/core/Container';
import Divider from '@material-ui/core/Divider';
import Drawer from '@material-ui/core/Drawer';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import Snackbar from '@material-ui/core/Snackbar';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import Alert from '@material-ui/lab/Alert';
import { containerImagePackagesApiRef } from '../../services/containerImagePackages.service';

type Rows = {
  name: string;
  tag: string;
  pull: string;
  updated: string;
  digest: string;
};

type Tag = {
  name: string;
  digest: string;
  id: string | undefined;
  lastUpdated: string;
};

type Tags = {
  digest: string;
  tags: Tag[];
};

const CopyToClipboardButton = ({ pullString }: { pullString: string }) => {
  const [open, setOpen] = useState(false);
  const handleClick = () => {
    setOpen(true);
    navigator.clipboard.writeText(pullString);
  };

  return (
    <>
      <>
        <Box onClick={handleClick}>
          <IconButton size="small">
            <FileCopyIcon fontSize="small" />
          </IconButton>
          {pullString}
        </Box>
        <Snackbar open={open} onClose={() => setOpen(false)} autoHideDuration={2000} message="Copied to clipboard" />
      </>
    </>
  );
};

const compareTags = (c: Tag, d: Tag) => {
  const cname = c.name.replace('-dev', '').replace(/.*-/gi, '');
  const dname = d.name.replace('-dev', '').replace(/.*-/gi, '');
  try {
    return compareVersions(cname, dname);
  } catch {
    return -1;
  }
};

const dedupe = (arr: string[] | undefined): string[] => {
  return [...new Set(arr)];
};

const majorVersions = (tags: Tags[]) => {
  const allMajorVersions = tags?.map(item => {
    return item.tags[0].name.replace('-dev', '').replace(/.*-/gi, '').split('.')[0];
  });
  return dedupe(allMajorVersions);
};

type image = {
  image: string;
  digest: string;
};

export const ContainerTags = () => {
  const { entity } = useEntity<ContainerImagesEntity>();
  const [state, setState] = useState<boolean>(false);

  const packageListingService = useApi(containerImagePackagesApiRef);

  const [image, setImage] = useState({ image: '', digest: '' });

  const toggleDrawer = (open: boolean, img?: image) => async (event: KeyboardEvent | MouseEvent) => {
    if (event.type === 'keydown' && 'key' in event && (event.key === 'Tab' || event.key === 'Shift')) {
      return;
    }
    if (img) {
      setImage({ image: img.image, digest: img.digest });
    } else {
      setImage({ image: '', digest: '' });
    }
    setState(open);
  };

  const [tags, setTags] = useState<Tags[]>([]);

  const CustomFilter = (props: { onFilterChanged: (rowId: any, value: string) => void; columnDef: any }) => {
    const handleClick = (version: string) => {
      props.onFilterChanged(props.columnDef.tableData.id, version);
    };

    const majorVersionsDeduped = majorVersions(tags);

    return (
      <>
        {majorVersionsDeduped.length > 1 && (
          <Grid container alignItems="center">
            {majorVersionsDeduped.map(version => (
              <Chip key={version} label={version} clickable onClick={() => handleClick(version)} />
            ))}
            <Grid item xs>
              <Divider orientation="vertical" flexItem />
            </Grid>
            <Chip label="Dev" clickable onClick={() => handleClick('dev')} />
            <Chip label="Prod" clickable onClick={() => handleClick('prod')} />
            <Grid item xs>
              <Divider orientation="vertical" flexItem />
            </Grid>
            <Chip color="secondary" label="Reset" clickable onClick={() => handleClick('')} />
          </Grid>
        )}
      </>
    );
  };

  const columns: TableColumn<Rows>[] = [
    {
      title: 'Name',
      field: 'name',
      width: '15%',
      hidden: true,
    },
    {
      title: 'Tag',
      field: 'tag',
      width: '20%',
      filterComponent: CustomFilter,
      customFilterAndSearch: (filter, rowData) => {
        if (filter === 'prod') {
          return !rowData.tag.split(', ')[0].includes('-dev');
        } else if (filter === 'dev') {
          return rowData.tag.split(', ')[0].includes('-dev');
        }
        return rowData.tag.split(', ')[0].split('.')[0].includes(filter);
      },
      render: (rowData: Rows) => rowData.tag.split(', ').map(item => <Chip key={item} size="small" label={item} />),
    },
    {
      title: 'Type',
      field: 'type',
      width: '15%',
      hidden: true,
    },
    {
      title: 'Cloudsmith Pull Value',
      field: 'pull',
      filtering: false,
      render: (rowData: Rows) => (
        <Grid container>
          <CopyToClipboardButton pullString={rowData.pull} />
        </Grid>
      ),
    },
    {
      title: 'Package List',
      field: 'packages',
      width: '10%',
      filtering: false,
      render: (rowData: Rows) => (
        <Button
          onClick={toggleDrawer(true, { image: rowData.name, digest: rowData.digest })}
          variant="contained"
          color="primary"
        >
          Packages
        </Button>
      ),
    },
    {
      title: 'Last Updated',
      field: 'updated',
      width: '10%',
      filtering: false,
      render: (rowData: Rows) => moment(rowData.updated).fromNow(),
    },
  ];

  useEffect(() => {
    const digests = new Map();

    for (const { digest, name, lastUpdated, id } of entity.spec.tags) {
      if (!digests.has(digest)) {
        digests.set(digest, { digest, tags: [] });
      }
      digests.get(digest).tags.push({ name, lastUpdated, id, digest });
    }
    const distinctDigests = [...digests.values()];
    distinctDigests
      .sort((a: Tags, b: Tags) => {
        b.tags.sort((c, d) => compareTags(c, d));
        a.tags.sort((c, d) => compareTags(c, d));

        // Ensure that `-dev` images are sorted below non-dev images
        const aName = a.tags[a.tags.length - 1].name;
        const bName = b.tags[b.tags.length - 1].name;
        const aNormalized = aName.includes('-dev') ? aName.replace('-dev', '.0') : aName.concat('.1');
        const bNormalized = bName.includes('-dev') ? bName.replace('-dev', '.0') : bName.concat('.1');

        try {
          return compareVersions(aNormalized.replace(/.*-/gi, ''), bNormalized.replace(/.*-/gi, ''));
        } catch {
          return 1;
        }
      })
      .reverse();

    setTags(distinctDigests);
  }, [entity]);

  return !tags ? (
    <Progress />
  ) : (
    <>
      Last Fetch: {moment(entity.spec.lastFetched).fromNow()}
      <Alert severity="warning">The below table displays Cloudsmith pull value strings.</Alert>
      <Table
        options={{
          search: false,
          paging: false,
          sorting: false,
          showTitle: false,
          toolbar: false,
          detailPanelType: 'single',
          filtering: majorVersions(tags).length > 1,
        }}
        columns={columns}
        detailPanel={[
          {
            tooltip: 'Expand',
            render: rowData => {
              return (
                <Grid>
                  <Grid container>
                    <Grid item xs={1} />
                    <Grid item xs={11}>
                      <Table
                        title={entity.metadata.name}
                        options={{ search: false, paging: false, sorting: false, showTitle: false, toolbar: false }}
                        columns={columns}
                        data={rowData.rowData.tag.split(', ').map(item => {
                          return {
                            name: entity.metadata.name,
                            tag: item,
                            pull: `${entity.spec.location}/${entity.spec.registry}/${entity.metadata.name}:${item}@${rowData.rowData.digest}`,
                            updated: rowData.rowData.updated,
                            digest: rowData.rowData.digest,
                          };
                        })}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              );
            },
          },
        ]}
        data={tags.map(tag => {
          return {
            name: entity.metadata.name,
            tag: tag.tags.map(item => item.name).join(', '),
            pull: `${entity.spec.location}/${entity.spec.registry}/${entity.metadata.name}:${tag.tags[0].name}@${tag.digest}`,
            type: tag.tags.every(el => el.name.includes('-dev')) ? 'dev' : 'prod',
            updated: tag.tags[tag.tags.length - 1].lastUpdated,
            digest: tag.digest,
          };
        })}
      />
      <Drawer anchor="right" open={state} onClose={toggleDrawer(false)}>
        <Container maxWidth="sm">
          <Table
            columns={[
              { title: 'Package', field: 'packageName' },
              { title: 'Version', field: 'packageVersion' },
            ]}
            title="Packages Listing"
            options={{
              search: false,
              paging: false,
            }}
            data={async query => {
              if (image.image) {
                return packageListingService.getPackageListing({ image: image.image, digest: image.digest });
              }
              return {
                data: [],
                page: query.page,
                totalCount: 0,
              };
            }}
          />
        </Container>
      </Drawer>
    </>
  );
};
